import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { AddressComponent } from '../../../../shared/components/address/address.component';
import { ListTableComponent } from '../../../../shared/components/list-table/list-table.component';
import { BaseComponent } from '../../../../shared/utils/base.component';
import { SectionsService } from '../../../../services/sections.service';
import { LeadDetailsService } from '../../../../services/lead-details.service';
import {
  STATIC_DATA,
  STATIC_JSON_DATA_PATH,
} from '../../../../shared/constants/static-data.constants';
import { performValidation } from '../../../../shared/utils/validation.util';
import { NgZorroModule } from '../../../../shared/modules/ng-zorro/ng-zorro.module';
import { CommonModule } from '@angular/common';
import { LoggerService } from '../../../../shared/services/logger.service';
import {
  LeadDetailIdentifiers,
  LeadDetailIdentifiersAddUpdateModel,
  LeadDetailIdentifiersModel,
} from '../../../../interfaces/lead-detail.model';
import { TableRowActionsModel } from '../../../../shared/interfaces/list.interface';
import { TableRowActions } from '../../../../shared/interfaces/actions';
import { AuthorizeDirective } from '../../../../shared/authorization/authorize.directive.';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { LookupValueItem } from '../../../../interfaces/lookup.interface';
import { NzAriaLabelDirective } from '../../../../shared/directives/nz-aria-label.directive';
import { AuthorizationUtility } from '../../../../shared/authorization/auth.utility';
import { NotificationService } from '../../../../shared/services/notification.service';
import { UserService } from '../../../../services/user.service';
import { DropdownSelectComponent } from '../../../../shared/components/dropdown-select/dropdown-select.component';
import { DialogConfig, DialogService } from '../../../../shared/services/dialog.service';
import { MfaService } from '../../../../services/mfa.service';
import { ErrorAlertService } from '../../../../shared/services/error-alert.service';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { ERROR_MESSAGES } from '../../../../shared/constants/error-message.constants';

@Component({
  selector: 'ppl-lead-detail-identifiers',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgZorroModule,
    ReactiveFormsModule,
    AddressComponent,
    ListTableComponent,
    AuthorizeDirective,
    ButtonComponent,
    NzAriaLabelDirective,
    DropdownSelectComponent,
    NzDatePickerModule,
  ],
  templateUrl: './lead-detail-identifiers.component.html',
  styleUrl: './lead-detail-identifiers.component.css',
})
export class LeadDetailIdentifiersComponent
  extends BaseComponent
  implements OnInit, OnChanges {
  mode = 'read';
  identifierForm!: FormGroup;
  filledInfo!: any;
  cols: any;
  gridData: any[] = [];
  identifiersList: LookupValueItem[] = [];
  copyOfGridData: any[] = [];
  steps!: any;
  entry = 'base';
  selectedLead: any;
  actionButtons: TableRowActionsModel = new TableRowActions();
  createdIdentifiers: string[] = [];
  approvedStatus: string = STATIC_DATA.APPROVED;
  deniedStatus: string = STATIC_DATA.DENIED;
  leadStatus = '';
  selectedIdentifierValue: string | undefined | null = null;
  ssnVisible = false;
  ssnError = false;
  cinError = false;
  originalIdentifierValue: string | null = null;
  isSectionEditable: boolean = false;
  infoData: any;
  isMobileView = window.innerWidth <= 768;
  canUnMask: boolean = false;
  canEditSSN: boolean = false;
  canBlockUnblock = false;
  viewHistory = false;
  historyData: any;
  currentHistoryCols: any[] = [];
  currentIdentifierType = '';
  currentHistoryParticipantId: number | null = null;

  get historyTableData(): any[] {
    const isExcludableType = this.currentIdentifierType === 'CIN' || this.currentIdentifierType === 'CPI';
    return (this.historyData ?? []).map((item: any) => ({
      ...item,
      modifiedDate: typeof item.modifiedDate === 'string' ? item.modifiedDate.split(' ')[0] : item.modifiedDate,
      excludedDisplay: item.toBeExcluded ? 'Yes' : 'No',
      actions: (isExcludableType && item.isActive === false && !this.isFinalizedStatus()) ? {
        exclude: !item.toBeExcluded,
        revert: item.toBeExcluded,
        excludeLabel: 'Exclude',
        revertLabel: 'Revert Exclusion'
      } : {}
    }));
  }
  historyCols = [
    { field: "value",        header: "Value",                jsonpath: "value"        },
    { field: "startDate",    header: "Effective Start Date", jsonpath: "startDate"    },
    { field: "endDate",      header: "Effective End Date",   jsonpath: "endDate"      },
    { field: "createdBy",    header: "Created By",           jsonpath: "createdBy"    },
    { field: "modifiedBy",   header: "Modified By",          jsonpath: "modifiedBy"   },
    { field: "modifiedDate", header: "Modified Date",        jsonpath: "modifiedDate" },
    { field: "excludedDisplay", header: "Excluded",          jsonpath: "excludedDisplay" }
  ];
  ssnHistoryCols = [
    { field: "modifiedBy",   header: "Last Modified By",   jsonpath: "modifiedBy"   },
    { field: "modifiedDate", header: "Last Modified Date", jsonpath: "modifiedDate" },
    { field: "value",        header: "SSN Value",          jsonpath: "value"        },
    { field: "status",       header: "SSN Status",         jsonpath: "status"       },
    { field: "comment",       header: "Comment",         jsonpath: "comment"       }
  ];
  private dialogSvc = inject(DialogService);

  @Input() pageId = '';
  @Input() tabId = '';
  @Input() programCode = '';
  @Input() readOnly = false;
  @Input() restrictReferralEdit = false;
  @Input() identifier = '';
  @Input() referralId = 0;
  @Input() type = '';
  @Input() category = '';
  @Input() set sectionData(data: string) {
    this.steps = data;
    /* console.log(this.steps); */
  }
  @Output() leadUpdated = new EventEmitter<boolean>();
  @ViewChild('identifierHistoryDialog') identifierHistoryDialog!: TemplateRef<any>;
  @ViewChild('blockConfirmDialog') blockConfirmDialog!: TemplateRef<unknown>;
  pendingBlockPayload: LeadDetailIdentifiersAddUpdateModel | null = null;
  blockConfirmDialogId: string | null = null;

  constructor(
    private fb: FormBuilder,
    private sectionService: SectionsService,
    private leadDetailsService: LeadDetailsService,
    private logger: LoggerService,
    private authUtility: AuthorizationUtility,
    private notificationService: NotificationService,
    private userService: UserService,
    private mfaService: MfaService,
    private errorAlertService: ErrorAlertService
  ) {
    super();
  }

  isFinalizedStatus(): boolean {
    return this.leadStatus === this.approvedStatus
      || (this.entry === STATIC_DATA.REFERRAL
        && (this.leadStatus === 'Review Complete' || this.leadStatus === STATIC_DATA.DENIED));
  }

  async ngOnInit() {
    this.isSectionEditable = this.readOnly || this.restrictReferralEdit ? false : this.authUtility.isSectionEditable(
      this.pageId,
      this.tabId,
      'Identifiers'
    );
    this.viewHistory = this.authUtility.isSectionEditable(this.pageId, this.tabId, 'ViewHistory');
    const userRoles = this.userService.getUserDetails()?.programs.find(pr => pr.program_code === this.programCode)?.roles;
    this.canUnMask = userRoles ? userRoles?.some(r => r.role_name === 'PPL Admin' || r.role_name === 'BAVAdmin' || r.role_name === 'Compliance') : false;
    this.canEditSSN = this.canUnMask;
    this.canBlockUnblock = userRoles ? userRoles.some(r => r.role_name === 'Compliance') : false;
    this.selectedLead = JSON.parse(
      this.userService.getSelectedLead()!
    );
    console.log(this.selectedLead);

    this.entry = this.userService.getLeadEntry() ?? '';
    /* console.log(this.entry); */
    this.createFormGroup();
    const excludedIdentifierCodes = ['NID', 'MHID','IMAID'];
    this.identifiersList = this.lookupService.getLookupValue('Identifier_type').filter(item => !excludedIdentifierCodes.includes(item.lkpValueCode));

    await this.getFromData();

    this.gridData = [];
    this.leadDetailsService.getLeadStatus().subscribe((status: string) => {
      this.leadStatus = status;

      if (this.isFinalizedStatus()) {
        this.actionButtons.edit = false;
        this.actionButtons.delete = false;
        this.actionButtons.history = false;
        this.actionButtons.unmask = this.canUnMask;
      } else {
        this.actionButtons.edit = true;
        this.actionButtons.delete = false;
        this.actionButtons.history = this.viewHistory;
        this.actionButtons.unmask = this.canUnMask;
      }
    });

    this.leadDetailsService.getUpdatedLeadData().subscribe((data) => {
      this.infoData = data?.responsedata?.infoData;
      this.gridData = data?.responsedata?.infoData?.identifiers?.filter((identifier: any) => identifier?.type !== 'NID' && identifier?.type !== 'MHID' && identifier?.type !== 'IMAID');

      this.copyAndTransformGridData(this.gridData);
      this.identifiersList = this.identifiersList.map((item) => {
        return {
          ...item,
          disabled: this.shouldDisableIdentifier(item),
        };
      });
    });
  }

  shouldDisableIdentifier = (option: any) => this.createdIdentifiers.includes(option.lkpValueCode);

  ngOnChanges(changes: SimpleChanges) {
    try {
      if (changes['sectionData'] && changes['sectionData'].currentValue) {
        this.secData = this.steps['identifiers'];
      }
    } catch (e) {
      this.logger.debug(
        'Error in getting section data and updating latest info',
        e
      );
    }
  }

  createFormGroup() {
    this.identifierForm = this.fb.group({
      leadIdentifierId: [],
      leadId: [],
      type: [],
      value: [],
      startDate: [],
      /* addlVal1: [],
      addlVal2: [] */
    });
    this.identifierForm.get('value')?.valueChanges.subscribe((val: string) => {
      this.syncStartDateEditability(val);
    });
  }

  showStartDate(): boolean {
    return this.selectedIdentifierValue === 'CIN' || this.selectedIdentifierValue === 'CPI';
  }

  getTodayLocalDate(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }

  onTypeChange(code: string) {
    if (code === 'CIN' || code === 'CPI') {
      this.identifierForm.get('startDate')?.setValue(this.getTodayLocalDate());
    }
  }

  syncStartDateEditability(value: string) {
    if (this.mode !== 'edit' || !this.showStartDate()) {
      return;
    }
    const startDateCtrl = this.identifierForm.get('startDate');
    const changed = (value ?? '').trim().toUpperCase() !== (this.originalIdentifierValue ?? '').trim().toUpperCase();
    if (changed) {
      startDateCtrl?.enable();
    } else {
      startDateCtrl?.disable();
    }
  }

  async getFromData() {
    try {
      if (this.entry) {
        const tempCols = await this.sectionService.getJsonConfigurationData(
          STATIC_JSON_DATA_PATH.LEAD_DETAILS_CONTACT_LIST_HEADERS,
          this.entry
        );
        this.cols = tempCols.identifiers.columns;
      }
    } catch (e) {
      this.logger.debug('Error in getting column data', e);
    }
  }

  copyAndTransformGridData(gridData: any[]) {
    this.createdIdentifiers = [];
    if (gridData) {
      const copyGridData = gridData.slice();

      this.copyOfGridData = copyGridData.map((item) => ({
        ...item,
        type: super.getLookupValue(this.identifiersList, item.type),
        modifiedDate: super.formatDateString(item.modifiedDate),
        canEditSSN: this.canEditSSN,
        disable: item.addlVal1?.toLowerCase().includes("blocked") || (!!item.addlVal2 && item.addlVal2 !== "all"),
        hideHistory: !((item.type === "CIN" || item.type === "CPI") && item.participantId),
        actions: (item.type === "SSN" && this.entry === STATIC_DATA.PROVIDER_ENROLLMENT) ? {
          block: this.canBlockUnblock && !(item.addlVal1?.toLowerCase() === 'blocked' && !item.addlVal2),
          unblock: this.canBlockUnblock && item.addlVal1?.toLowerCase() === 'blocked' && !item.addlVal2,
          history: this.canBlockUnblock,
          blockLabel: 'Block All',
          unblockLabel: 'Unblock All'
        } : {}
      }));

      copyGridData.forEach((item) => {
        this.createdIdentifiers.push(item.type);
      });
      if (!this.isFinalizedStatus()) {
        this.actionButtons.edit = true;
        this.actionButtons.history = this.viewHistory;
        this.actionButtons.delete = false;
        this.actionButtons.unmask = this.canUnMask;
      } else {
        this.actionButtons.edit = false;
        this.actionButtons.delete = false;
        this.actionButtons.history = false;
        this.actionButtons.unmask = this.canUnMask;
      }
    } else {
      this.copyOfGridData = [];
      this.actionButtons.delete = false;
      this.actionButtons.edit = false;
      this.actionButtons.history = false;
      this.actionButtons.unmask = this.canUnMask;
    }
  }

  cancelEdit() {
    this.mode = 'read';
    this.validationErrors = {};
    this.ssnError = false;
    this.cinError = false;
    this.originalIdentifierValue = null;
    this.identifierForm.get('startDate')?.enable();
    this.identifierForm.reset();
  }

  action(event: any) {
    console.log('acto', event);

    const selectedId = this.extractId(event.data);
    this.selectedIdentifierValue = this.identifiersList.find(
      (item) => item.lkpValueName === event.data.type
    )?.lkpValueCode;

    //(this.entry === 'referral')? event.data.referralIdentifierId : event.data.leadIdentifierId;
    const selectedData = this.gridData.find((row: any) => {
      if (
        row.leadIdentifierId === selectedId ||
        row.referralIdentifierId === selectedId ||
        row.enrollmentIdentifierId === selectedId ||
        row.providerIdentifierId === selectedId
      ) {
        return row;
      }
    });

    if (event && event.action === 'edit') {
      if (this.userService.showMfaPopup()) {
        this.doMfa(selectedData, selectedId, 'edit');
      } else {
        this.editInfo(selectedData, selectedId);
      }
    }

    if (event && event.action === 'delete') {
      if (this.userService.showMfaPopup()) {
        this.doMfa(selectedData, selectedId, 'delete');
      } else {
        this.deleteInfo(event, selectedId, selectedData);
      }
    }

    if (event && event.action === 'unmask') {
      const payload = this.getUnMaskAPIPayload('SSN');

      this.leadDetailsService.unmaskIdentifier(this.programCode, payload).subscribe(data => {
        const selectedgridItem = this.copyOfGridData.find(cgd => cgd.type === 'Social Security Number (SSN)' || cgd.type === 'SSN');
        selectedgridItem.value = data.responsedata;
      });
    }

    if (event && event.action === 'history') {
      const historyId = event.data.participantId ?? this.selectedLead;
      this.openHistoryDialog(historyId, event.data.type);
    }

    if (event && event.action === 'block') {
      this.pendingBlockPayload = {
        id: this.selectedLead,
        identifierId: selectedId,
        programCode: this.programCode,
        referralId: this.referralId,
        identifier: {
          type: selectedData?.type ?? 'SSN',
          value: selectedData?.value,
          modifiedDate: selectedData?.modifiedDate,
          addlVal1: 'blocked'
        }
      };
      this.openBlockConfirmDialog();
    }

    if (event && event.action === 'unblock') {
      const payload: LeadDetailIdentifiersAddUpdateModel = {
        id: this.selectedLead,
        identifierId: selectedId,
        programCode: this.programCode,
        referralId: this.referralId,
        identifier: {
          type: selectedData?.type ?? 'SSN',
          value: selectedData?.value,
          modifiedDate: selectedData?.modifiedDate,
          addlVal1: null
        }
      };
      this.leadDetailsService.blockUnblockIdentifier(payload, this.entry).subscribe(() => {
        this.leadUpdated.emit(true);
      });
    }

    if (event && event.action === 'exclude') {
      this.leadDetailsService.excludeParticipantIdentifier(
        this.programCode,
        { participantIdentifierId: event.data.participantIdentifierId, toBeExcluded: true },
        ERROR_MESSAGES.SUCCESS_EXCLUDE_PARTICIPANT_IDENTIFIER
      ).subscribe(() => this.refreshHistory());
    }

    if (event && event.action === 'revertExclusion') {
      this.leadDetailsService.excludeParticipantIdentifier(
        this.programCode,
        { participantIdentifierId: event.data.participantIdentifierId, toBeExcluded: false },
        ERROR_MESSAGES.SUCCESS_REVERT_PARTICIPANT_IDENTIFIER_EXCLUSION
      ).subscribe(() => this.refreshHistory());
    }
  }

  deleteInfo(event: any, selectedId: number, selectedData: any) {
    console.log('selectedData', selectedData);
    if (this.entry === STATIC_DATA.PROVIDER && event.data.type === 'Social Security Number (SSN)' && !this.infoData.isProvidingServices) {
      this.notificationService.error(
        'Error',
        'Cannot delete SSN if Providing CDPAP Services is No'
      );
    } else {
      if (selectedData) {
        this.delete(selectedId);
      }
    }
  }

  editInfo(selectedData: any, selectedId: number) {
    this.mode = 'edit';
    this.ssnError = false;
    this.cinError = false;

    if (selectedData) {
      selectedData = { ...selectedData };
      console.log('selectedData', selectedData);
      this.originalIdentifierValue = selectedData.value ?? null;
      if (selectedData.type === 'SSN' && this.canUnMask) {
        const payload = this.getUnMaskAPIPayload('SSN');

        this.leadDetailsService.unmaskIdentifier(this.programCode, payload).subscribe(data => {
          selectedData.value = data.responsedata;
          this.identifierForm.patchValue(selectedData);
        });
      }
      else {
        this.identifierForm.patchValue(selectedData);
      }

      if (selectedData.type === 'CIN' || selectedData.type === 'CPI') {
        this.identifierForm.get('startDate')?.setValue(this.getTodayLocalDate());
      }

      if (
        this.entry === STATIC_DATA.REFERRAL ||
        this.entry === STATIC_DATA.PARTICIPANT_ENROLLMENT ||
        this.entry === STATIC_DATA.PROVIDER_ENROLLMENT
      ) {
        this.identifierForm.patchValue({
          leadIdentifierId: selectedId,
          leadId: this.selectedLead,
        });
      }
    }
  }

  doMfa(selectedData: any, selectedId: number, action: 'edit' | 'delete') {
    if (this.userService.showMfaPopup()) {
      this.mfaService.doMfa().subscribe(success => {
        if (success) {
          if (action === 'delete') {
            this.deleteInfo(null, selectedId, selectedData);
          } else {
            this.editInfo(selectedData, selectedId);
          }
        } else {
           this.errorAlertService.openModal('error', 'Error', 'MFA verification failed or cancelled.');
        }
      });
    } else {
      if (action === 'delete') {
        this.deleteInfo(null, selectedId, selectedData);
      } else {
        this.editInfo(selectedData, selectedId);
      }
    }
  }

  doMfaAdd() {
    if (this.userService.showMfaPopup()) {
      this.mfaService.doMfa().subscribe(success => {
        if (success) {
          this.add();
        } else {
           this.errorAlertService.openModal('error', 'Error', 'MFA verification failed or cancelled.');
        }
      });
    } else {
      this.add();
    }
  }

  getUnMaskAPIPayload(identifierType: string) {
    return {
      "programCode": this.programCode,
      "identifier": this.identifier.toString(),
      "source": 'PPL@Home',
      "identifierType": identifierType,
      "userType": this.type,
      "userCategory": this.category
    };
  }

  limitDigits(event: any): void {
    let value = event.target.value.replace(/\D/g, '');
    if (value.length > 9) {
      value = value.slice(0, 9);
    }

    event.target.value = value;
    this.identifierForm.get('value')?.setValue(value);
  }

  add() {
    this.mode = 'add';
    this.ssnError = false;
    this.cinError = false;
    this.selectedIdentifierValue = null;
    this.originalIdentifierValue = null;
    this.identifierForm.get('startDate')?.enable();
    this.identifierForm.reset();
    this.identifierForm.controls['leadId'].setValue(this.selectedLead);
  }

  save() {
    const pageData = this.identifierForm.value;
    this.ssnError = false;
    this.cinError = false;
    if (pageData.type === 'SSN') {
      const ssn = pageData.value.replace(/-/g, '');
      const regex = /^\d{9}$/;
      const reservedSsn = ['078051120', '721074426', '219099999'];
      const ssnRegex =
        /^(?!666|000|9\d{2})(?!.*(\d)(?:\1){8})(?!123456789)\d{3}(?!00)\d{2}(?!0000)\d{4}$/;
      // Check if ssn has exactly 9 digits first
      if (!regex.test(ssn)) {
        this.ssnError = true;
      }
      // Validate the ssn against the main regex
      if (!ssnRegex.test(pageData.value)) {
        this.ssnError = true;
      }
      if (reservedSsn.includes(pageData.value)) {
        this.ssnError = true;
      }
    }
    if (pageData.type === 'CIN') {
      const cinRegex = /^[A-Za-z]{2}\d{5}[A-Za-z]$/;
      if (!cinRegex.test(pageData.value)) {
        this.cinError = true;
      }
    }
    try {
      this.validationErrors = performValidation(
        this.identifierForm.value,
        this.secData,
        'Save', undefined, undefined, this.sectionService.getFlow(), this.sectionService.getEnrollmentType()
      );
      this.logger.debug('Error in validations', this.validationErrors);
    } catch {
      this.logger.debug('Error in validations');
    }
    if (this.identifierForm.get('startDate')?.disabled) {
      delete this.validationErrors['startDate'];
    }
    if (Object.keys(this.validationErrors).length === 0 && !this.ssnError && !this.cinError) {
      const payload = this.preparePayload(pageData);
      this.leadDetailsService
        .createUpdateLeadIdentifier(payload, this.entry)
        .subscribe({
          next: () => {
            this.logger.info('Lead Identifier saved successfully');
            this.leadUpdated.emit(true);
            this.cancelEdit();
          },
          error: () => {
            this.leadUpdated.emit(true);
            this.cancelEdit();
          },
        });
    }
  }

  extractId(identifier: any) {
    let id = identifier.leadIdentifierId;
    if (this.entry === STATIC_DATA.REFERRAL) {
      id = identifier.referralIdentifierId;
    }
    if (this.entry === STATIC_DATA.PARTICIPANT_ENROLLMENT) {
      id = identifier.enrollmentIdentifierId;
    }
    if (this.entry === STATIC_DATA.PROVIDER_ENROLLMENT) {
      id = identifier.providerIdentifierId;
    }
    return id;
  }

  delete(identifierId: number) {
    /* let id = identifier?.leadIdentifierId;
    if (this.entry === STATIC_DATA.REFERRAL) {
      id = identifier.referralIdentifierId;
    }
    if(this.entry === STATIC_DATA.PARTICIPANT_ENROLLMENT){
      id = identifier.enrollmentIdentifierId
    } */

    //let id = this.extractId(identifier);

    this.leadDetailsService
      .deleteLeadIdentifier(identifierId, this.entry, this.programCode)
      .subscribe(() => {
        this.logger.info('Identified deleted successfully');
        this.leadUpdated.emit(true);
      });
  }

  preparePayload(
    source: LeadDetailIdentifiersModel
  ): LeadDetailIdentifiersAddUpdateModel {
    const { leadIdentifierId, ...identifierData } = source;
    const leadDetailIdentifierId =
      this.mode === 'edit' ? leadIdentifierId : null;

    if (identifierData.type === 'CIN') {
      identifierData.value = identifierData.value.toUpperCase();
    }
    if ((identifierData.startDate as unknown) instanceof Date) {
      identifierData.startDate = this.dateFormatter.formatDateToUSDateString(identifierData.startDate as unknown as Date);
    }
    const existingIdentifier = this.gridData
      ? this.gridData.find((row) => {
        if (
          row.leadIdentifierId === leadDetailIdentifierId ||
          row.referralIdentifierId === leadDetailIdentifierId ||
          row.enrollmentIdentifierId === leadDetailIdentifierId ||
          row.providerIdentifierId === leadDetailIdentifierId
        ) {
          return row;
        }
      })
      : null;

    return {
      id: this.selectedLead,
      identifierId: leadDetailIdentifierId,
      programCode: this.programCode,
      referralId: this.referralId,
      identifier: {
        ...(identifierData as LeadDetailIdentifiers),
        modifiedDate: existingIdentifier?.modifiedDate,
      },
    };
  }

  getAddedIdentifier(identifier: string): boolean {
    const created = this.createdIdentifiers.find(
      (identifier) => identifier.toLowerCase() === identifier.toLowerCase()
    );
    this.logger.info('Added identifier', this.createdIdentifiers, identifier);
    if (created) {
      return true;
    } else {
      return false;
    }
  }

  getTooltipTitle() {
    const selectedLkpValueName = this.identifiersList.find(
      (identifer) => this.selectedIdentifierValue === identifer.lkpValueCode
    )?.lkpValueName;

    return selectedLkpValueName ? `Please Enter ${selectedLkpValueName}` : '';
  }

  openHistoryDialog(participantId: any, type: any){
    let identifierType = '';
    if(type === "Client Identification Number(CIN)"){
      identifierType = 'CIN';
      this.currentHistoryCols = this.historyCols;
    } else if (type === "Social Security Number (SSN)" || type === "SSN") {
      identifierType = 'SSN';
      this.currentHistoryCols = this.ssnHistoryCols;
    } else if (type === "Consumer MCO Plan ID") {
      identifierType = 'CPI';
      this.currentHistoryCols = this.historyCols;
    }

    this.currentIdentifierType = identifierType;
    this.currentHistoryParticipantId = participantId;

    const isProviderSSN = identifierType === 'SSN' && this.entry === STATIC_DATA.PROVIDER_ENROLLMENT;
    const historyCall = isProviderSSN
      ? this.leadDetailsService.getProviderIdentifierHistory(this.programCode, this.selectedLead, identifierType)
      : this.leadDetailsService.getIdentifierHistory(this.programCode, participantId, identifierType);

    historyCall.subscribe((data) => {
        if(data.status == 200){
          this.historyData = data.responsedata;
            const dialogConfig: DialogConfig = {
              title: `${type} History`,
              isOkText: 'Ok',
              size: 'large',
              position: 'top',
              showClose: true,
              showButtons: false,
              styleClass: 'split-auth-data'
            };
        
            this.dialogSvc.openDialog(
              this.identifierHistoryDialog,
              dialogConfig,
              () => { return; },
              () => { return; }
            );
        }
      })
  }

  refreshHistory() {
    if (this.currentHistoryParticipantId === null) {
      return;
    }
    this.leadDetailsService
      .getIdentifierHistory(this.programCode, this.currentHistoryParticipantId, this.currentIdentifierType)
      .subscribe((data) => {
        if (data.status == 200) {
          this.historyData = data.responsedata;
        }
      });
  }

  openBlockConfirmDialog() {
    const dialogConfig: DialogConfig = {
      title: 'Confirmation',
      size: 'medium',
      position: 'top',
      showClose: true,
      showButtons: true,
      isOkText: 'Yes',
      cancelText: 'No',
    };
    this.blockConfirmDialogId = this.dialogSvc.openDialog(
      this.blockConfirmDialog,
      dialogConfig,
      () => this.confirmBlock(),
      () => { this.pendingBlockPayload = null; this.blockConfirmDialogId = null; }
    );
  }

  confirmBlock() {
    if (this.pendingBlockPayload) {
      this.leadDetailsService.blockUnblockIdentifier(this.pendingBlockPayload, this.entry).subscribe(() => {
        this.leadUpdated.emit(true);
      });
    }
    this.pendingBlockPayload = null;
    this.blockConfirmDialogId = null;
  }

}
